export type PathwayKey = 'async' | 'e2e'
export type WorkstreamVariant = 'people' | 'product' | 'brand'
export type ModalTemplate = 'workstream-agenda' | 'agenda-table' | 'workstream-detail' | 'banded-workstream-detail'

export interface TextSegment {
  text?: string
  bold?: string
}

export interface ModalRef {
  template: ModalTemplate
  id: string
}

export interface PathwayActions {
  agenda?: ModalRef
  detail?: ModalRef
}

export interface PathwayTextColumn {
  title: string
  segments?: TextSegment[]
  bullets?: string[]
}

export interface WorkstreamCardData {
  key: string
  title: string
  segments: TextSegment[]
  variant: WorkstreamVariant
  actions: PathwayActions
}

export interface WorkflowStepCardData {
  number: number
  segments: TextSegment[]
}

export interface PreworkSectionData {
  title: string
  durationLabel: string
  textColumns: PathwayTextColumn[]
  workstreamCards?: WorkstreamCardData[]
  workflow?: { title: string; steps: WorkflowStepCardData[] }
}

export interface SprintDayCardData {
  key: string
  dayLabel: string
  title?: string
  segments: TextSegment[]
  number: number
  optionalBadge?: boolean
  actions?: PathwayActions
}

export interface SprintSectionData {
  title: string
  durationLabel: string
  paragraphColumns: TextSegment[][]
  dayCards: SprintDayCardData[]
  sharedActions?: PathwayActions
  layout: 'row' | 'grid'
}

export interface PathwayData {
  key: PathwayKey
  pillLabel: string
  name: string
  segments: TextSegment[]
  stackedGradientPanels: boolean
  prework: PreworkSectionData
  sprint: SprintSectionData
}

export const explorePathwaysHeading = 'Explore both pathways'

export const explorePathwaysSubtext: TextSegment[] = [
  { text: 'You can choose between ' },
  { bold: 'Async Exploration With Moment of Fusion' },
  { text: ' and ' },
  { bold: 'End-to-End Co-Creation' },
  { text: ' to immerse yourself in the details and learn more about these working modes.' },
]

export const pathwaysData: Record<PathwayKey, PathwayData> = {
  async: {
    key: 'async',
    pillLabel: 'Async Exploration With Moment of Fusion',
    name: 'Async Exploration With Moment of Fusion',
    segments: [
      { text: 'In this version, 70% of the work takes place ' },
      { bold: 'asynchronously' },
      { text: ', via 3 workstreams orchestrated by the ' },
      { bold: 'Brand Lead' },
      { text: '.' },
    ],
    stackedGradientPanels: true,
    prework: {
      title: 'Prework',
      durationLabel: '4–8 weeks',
      textColumns: [
        {
          title: 'Review and gather material',
          segments: [
            {
              text: 'Each workstream completes their prework within 4-8 weeks, as a combination of asynchronous research and virtual sessions.',
            },
          ],
        },
        {
          title: 'Collaborate and connect',
          segments: [
            {
              text: 'Workstreams come together to share progress and cross-pollinate through a minimum of 2 virtual sessions leading up to the sprint.',
            },
          ],
        },
        {
          title: 'Prepare for sprint',
          segments: [{ text: 'These sessions together with the sprint help complete the remaining 30% of the work.' }],
        },
      ],
      workstreamCards: [
        {
          key: 'people',
          title: 'People Workstream',
          segments: [{ text: 'Identify sources of relevance for growth audience target' }],
          variant: 'people',
          actions: {
            agenda: { template: 'workstream-agenda', id: 'async-people-agenda' },
            detail: { template: 'workstream-detail', id: 'async-people-detail' },
          },
        },
        {
          key: 'product',
          title: 'Product Workstream',
          segments: [{ text: 'Deliver a differentiated product experience' }],
          variant: 'product',
          actions: {
            agenda: { template: 'workstream-agenda', id: 'async-product-agenda' },
            detail: { template: 'workstream-detail', id: 'async-product-detail' },
          },
        },
        {
          key: 'brand',
          title: 'Brand Workstream',
          segments: [{ text: 'Identify foundations for brand saliency and love' }],
          variant: 'brand',
          actions: {
            agenda: { template: 'workstream-agenda', id: 'async-brand-agenda' },
            detail: { template: 'workstream-detail', id: 'async-brand-detail' },
          },
        },
      ],
    },
    sprint: {
      title: 'The Sprint',
      durationLabel: '2 days',
      paragraphColumns: [
        [
          {
            text: 'At the end of the prework period, each individual team produces their most prominent routes but not definite answers to their workstream exploration.',
          },
        ],
        [
          {
            text: 'These will be fully finalized in collaboration with the other workstreams when they come together for a physical 2-day sprint.',
          },
        ],
      ],
      dayCards: [
        {
          key: 'day1',
          dayLabel: 'Day 1',
          segments: [
            {
              text: 'Each workstream presents their stimuli and proposals. 3 cross functional teams now all take a shot at completing the 3 next templates (Building Relevance, Vow to Consumers, Mini Manifesto), landing on a common BVA.',
            },
          ],
          number: 1,
        },
        {
          key: 'day2',
          dayLabel: 'Day 2',
          segments: [
            {
              text: 'Check-in with sponsor and vested OUs for feedback, iterate on feedback and finalize BVA. Assess implications on: innovation pathways guidance, claims needs, critical occasions/demand spaces, engagement (comms and assets).',
            },
          ],
          number: 2,
        },
      ],
      sharedActions: {
        agenda: { template: 'workstream-agenda', id: 'async-sprint-agenda' },
        detail: { template: 'workstream-detail', id: 'async-sprint-detail' },
      },
      layout: 'row',
    },
  },
  e2e: {
    key: 'e2e',
    pillLabel: 'End-to-End Co-Creation',
    name: 'End-to-End Co-Creation',
    segments: [{ text: "A smaller team, and a high-energy sprint. Here's how it works:" }],
    stackedGradientPanels: true,
    prework: {
      title: 'Prework',
      durationLabel: '4–8 weeks',
      textColumns: [
        {
          title: 'Collaborate and connect',
          segments: [
            { text: 'In this version, the prework is conducted by a small core team (approx. ' },
            { bold: '6 people' },
            { text: ') working' },
            { bold: ' with external partners' },
            { text: ' to prepare the stimuli for the sprint across all 3 parts: People, Product and Brand.' },
          ],
        },
        {
          title: 'The core team should comprise of:',
          bullets: [
            'Global Brand Lead',
            'Global Creative Strategist',
            'Human Insights Lead',
            'Innovation Lead',
            'WPP Strategist',
            'PMO',
          ],
        },
        {
          title: 'Onboarding & Immersion',
          segments: [
            {
              text: 'The ',
            },
            { bold: 'pre-work lasts for 4-8 weeks' },
            {
              text: ', during which there is some onboarding of the other sprint participants to the key findings as well as assigned empathy homework so that everyone can do a deep immersion across the 3 parts.',
            },
          ],
        },
      ],
      workflow: {
        title: 'Overall Prework Workflow - Core Team with External Partners',
        steps: [
          { number: 1, segments: [{ text: 'Assessment available data and curate most interesting content' }] },
          { number: 2, segments: [{ text: 'Commission fieldwork for missing critical input' }] },
          { number: 3, segments: [{ text: 'Immersions (for People, Product, Brand separately)' }] },
          {
            number: 4,
            segments: [
              {
                text: 'Virtual kickoff session to onboard all participants to workshop mission, milestones towards sprint, booking prework sessions',
              },
            ],
          },
          {
            number: 5,
            segments: [
              {
                text: 'Core team virtual session to distill the freaky facts used as stimuli and design the onboarding session',
              },
            ],
          },
          {
            number: 6,
            segments: [
              { text: 'Core team works with facilitation partner to finalize agenda, exercises and logistics' },
            ],
          },
          {
            number: 7,
            segments: [
              {
                text: 'Virtual onboarding session for all participants to immerse them into most interesting data points + assign immersion homework',
              },
            ],
          },
        ],
      },
    },
    sprint: {
      title: 'The Sprint',
      durationLabel: '4.5 days',
      paragraphColumns: [
        [
          { text: 'This allows for a POV to start being shaped but all the execution work takes place during ' },
          { bold: '4.5 days of in-person sprinting.' },
        ],
        [
          { text: 'Approximately ' },
          { bold: '15 people' },
          {
            text: ' participate in the sprint, indicatively: core team, 2-3 lead OU marketing reps, 2-3 strategists from WPP (BEI).',
          },
        ],
      ],
      dayCards: [
        {
          key: 'day1',
          dayLabel: 'Day 1',
          title: 'People',
          segments: [{ text: 'Identify sources of relevance for our Intenders (& Neutrals)' }],
          number: 1,
          actions: {
            agenda: { template: 'agenda-table', id: 'e2e-day1-agenda' },
            detail: { template: 'banded-workstream-detail', id: 'e2e-day1-detail' },
          },
        },
        {
          key: 'day2',
          dayLabel: 'Day 2',
          title: 'Product',
          segments: [{ text: 'Deliver a differentiated product experience' }],
          number: 2,
          actions: {
            agenda: { template: 'agenda-table', id: 'e2e-day2-agenda' },
            detail: { template: 'banded-workstream-detail', id: 'e2e-day2-detail' },
          },
        },
        {
          key: 'day3',
          dayLabel: 'Day 3',
          title: 'Brand',
          segments: [{ text: 'Identify foundations for brand saliency and love' }],
          number: 3,
          actions: {
            agenda: { template: 'agenda-table', id: 'e2e-day3-agenda' },
            detail: { template: 'banded-workstream-detail', id: 'e2e-day3-detail' },
          },
        },
        {
          key: 'day4',
          dayLabel: 'Day 4',
          title: 'Meaningful Brand Role',
          segments: [{ text: 'Converge and land on summary template' }],
          number: 4,
          actions: {
            agenda: { template: 'agenda-table', id: 'e2e-day4-agenda' },
            detail: { template: 'banded-workstream-detail', id: 'e2e-day4-detail' },
          },
        },
        {
          key: 'day5',
          dayLabel: 'Day 5',
          title: 'Implication Assessment',
          optionalBadge: true,
          segments: [
            {
              text: 'Pressure-test the finalized BVA against priority demand spaces, designing experiences, and product innovation. Outline next steps for approvals from OU stakeholders, sponsor and Global CMO.',
            },
          ],
          number: 5,
          actions: {
            agenda: { template: 'agenda-table', id: 'e2e-day5-agenda' },
          },
        },
      ],
      layout: 'grid',
    },
  },
}

export interface ImportantNoteColumn {
  paragraphs: TextSegment[][]
}

export const importantNoteColumns: ImportantNoteColumn[] = [
  {
    paragraphs: [
      [{ bold: 'The last batch of templates is meant to fuse all the data from your previous work.' }],
      [
        {
          text: 'Therefore, it is critical that you do not treat them as ad hoc exercises, but as the continuation and build-up of all the work done so far.',
        },
      ],
    ],
  },
  {
    paragraphs: [
      [
        { text: 'Before beginning to work on them, pull up the ' },
        { bold: 'completed templates on People, Product and Brand.' },
      ],
      [
        { text: 'Encourage the ' },
        { bold: 'team to study them again very carefully' },
        { text: ' and make sure everyone is clear and aligned on their content.' },
      ],
    ],
  },
  {
    paragraphs: [
      [
        { text: "Now it's time for you to complete the fusion templates. Do a quick run-through and sense check: " },
        { bold: 'do they closely and clearly reflect the outputs of the previous phase?' },
      ],
    ],
  },
  {
    paragraphs: [
      [
        { text: 'This is essential for ultimately leading you to a ' },
        { bold: 'Meaningful Brand Role' },
        { text: ' that is ' },
        { bold: 'people-first, strongly product-centric' },
        { text: ', defined by our ' },
        { bold: 'enterprise expertise' },
        { text: ', and ' },
        { bold: 'fiercely rooted in data.' },
      ],
    ],
  },
]
